import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Send, Calendar, Plus, Loader2, Trash2, ExternalLink, Link2, Music, Camera, Facebook, MessageCircle,
  ArrowLeft, ShieldAlert, CheckCircle2, Clock, AlertTriangle,
  Twitter, Youtube, Linkedin, BookMarked, Upload, X as XIcon, Sparkles, FileText,
} from 'lucide-react';

const PLATFORM_META = {
  tiktok:          { label: 'TikTok',         icon: Music,       color: 'bg-black text-white' },
  instagram:       { label: 'Instagram',      icon: Camera,      color: 'bg-pink-500 text-white' },
  facebook:        { label: 'Facebook',       icon: Facebook,    color: 'bg-blue-600 text-white' },
  snapchat:        { label: 'Snapchat',       icon: MessageCircle,color: 'bg-yellow-400 text-black' },
  x:               { label: 'X (Twitter)',    icon: Twitter,     color: 'bg-slate-900 text-white' },
  youtube:         { label: 'YouTube',        icon: Youtube,     color: 'bg-red-600 text-white' },
  youtube_shorts:  { label: 'YouTube Shorts', icon: Youtube,     color: 'bg-red-500 text-white' },
  linkedin:        { label: 'LinkedIn',       icon: Linkedin,    color: 'bg-blue-800 text-white' },
  pinterest:       { label: 'Pinterest',      icon: BookMarked,  color: 'bg-red-700 text-white' },
};

const STATUS_META = {
  draft:                { label: 'Draft',        className: 'bg-slate-100 text-slate-600' },
  scheduled:            { label: 'Scheduled',    className: 'bg-blue-100 text-blue-700' },
  published:            { label: 'Published',    className: 'bg-emerald-100 text-emerald-700' },
  pending_credentials:  { label: 'Needs API key',className: 'bg-amber-100 text-amber-700' },
  simulated:            { label: 'Simulated',    className: 'bg-purple-100 text-purple-700' },
  error:                { label: 'Failed',       className: 'bg-red-100 text-red-700' },
};

const SocialHubPage = () => {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [tab, setTab] = useState('composer');
  const [accounts, setAccounts] = useState([]);
  const [posts, setPosts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [supportedPlatforms, setSupportedPlatforms] = useState({});

  // Composer state
  const [draft, setDraft] = useState({ platforms: [], caption: '', hashtags: '', link_url: '', schedule_at: '', media_urls: [] });
  const [saving, setSaving] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);

  // Templates state
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [pickedTemplate, setPickedTemplate] = useState(null);
  const [generatingCaption, setGeneratingCaption] = useState(false);

  // Connect account dialog
  const [connectOpen, setConnectOpen] = useState(false);
  const [connectDraft, setConnectDraft] = useState({ platform: 'tiktok', handle: '', access_token: '', account_id: '' });

  const load = async () => {
    setLoading(true);
    try {
      const [accRes, postsRes, sumRes] = await Promise.all([
        api.get('/social/accounts'),
        api.get('/social/posts'),
        api.get('/social/analytics/summary'),
      ]);
      setAccounts(accRes.data.accounts || []);
      setSupportedPlatforms(accRes.data.supported_platforms || {});
      setPosts(postsRes.data.posts || []);
      setSummary(sumRes.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("You don't have Social Hub access.");
      } else {
        toast.error('Failed to load Social Hub');
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const togglePlatform = (p) => {
    setDraft((prev) => ({
      ...prev,
      platforms: prev.platforms.includes(p) ? prev.platforms.filter((x) => x !== p) : [...prev.platforms, p],
    }));
  };

  const createPost = async (publishNow) => {
    if (!draft.caption.trim()) { toast.error('Add a caption'); return; }
    if (draft.platforms.length === 0) { toast.error('Choose at least one platform'); return; }
    setSaving(true);
    try {
      const payload = {
        platforms: draft.platforms,
        caption: draft.caption,
        hashtags: draft.hashtags.split(/\s+/).filter((h) => h.startsWith('#')),
        media_urls: draft.media_urls,
        link_url: draft.link_url || null,
        schedule_at: publishNow ? null : (draft.schedule_at || null),
      };
      const r = await api.post('/social/posts', payload);
      if (publishNow) {
        const pub = await api.post(`/social/posts/${r.data.id}/publish`);
        const anyPending = Object.values(pub.data.results || {}).some((x) => x.status === 'pending_credentials');
        if (anyPending) {
          toast.warning('Some platforms need API credentials — see the setup guide.');
        } else {
          toast.success('Published!');
        }
      } else {
        toast.success(draft.schedule_at ? 'Scheduled!' : 'Saved as draft');
      }
      setDraft({ platforms: [], caption: '', hashtags: '', link_url: '', schedule_at: '', media_urls: [] });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create post');
    } finally { setSaving(false); }
  };

  const uploadMedia = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingMedia(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/files/upload?scope=social', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Mark public so the platform APIs can fetch it, then attach
      await api.patch(`/files/${r.data.id}/public?public=true`).catch(() => {});
      setDraft((d) => ({ ...d, media_urls: [...d.media_urls, r.data.url] }));
      toast.success('Media uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingMedia(false);
      e.target.value = '';
    }
  };

  const removeMedia = (url) => setDraft((d) => ({ ...d, media_urls: d.media_urls.filter((u) => u !== url) }));

  const loadTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const r = await api.get('/social/templates/materials');
      setTemplates(r.data.templates || []);
    } catch (err) {
      toast.error('Could not load templates');
    } finally { setTemplatesLoading(false); }
  };

  const generateFromTemplate = async (materialId) => {
    if (draft.platforms.length === 0) {
      toast.error('Pick at least one target platform first');
      setTab('composer');
      return;
    }
    setGeneratingCaption(true);
    try {
      const r = await api.post('/social/templates/generate-post', {
        material_id: materialId,
        platforms: draft.platforms,
        tone: 'warm-and-fun',
      });
      // Take the LONGEST caption to seed the composer (respects the largest limit)
      const captions = r.data.captions || {};
      const longest = Object.values(captions).sort((a, b) => (b.caption?.length || 0) - (a.caption?.length || 0))[0];
      const sharedTags = (r.data.shared_hashtags || []).join(' ');
      const perPlatformTags = Object.values(captions).flatMap((c) => c.hashtags || []);
      const uniqueTags = Array.from(new Set([...(sharedTags.split(/\s+/)), ...perPlatformTags])).filter(Boolean).join(' ');
      setDraft((d) => ({ ...d, caption: longest?.caption || '', hashtags: uniqueTags }));
      setPickedTemplate(materialId);
      setTab('composer');
      toast.success(r.data.fallback ? 'Draft ready (using fallback text)' : 'AI caption ready — polish and publish!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not generate caption');
    } finally { setGeneratingCaption(false); }
  };

  const publishPost = async (id) => {
    try {
      const pub = await api.post(`/social/posts/${id}/publish`);
      const results = pub.data.results || {};
      const pending = Object.entries(results).filter(([, v]) => v.status === 'pending_credentials');
      if (pending.length > 0) toast.warning(`${pending.length} platform(s) need API keys`);
      else toast.success('Published!');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Publish failed'); }
  };

  const deletePost = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await api.delete(`/social/posts/${id}`);
      toast.success('Deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  const connectAccount = async () => {
    if (!connectDraft.handle) { toast.error('Enter a handle'); return; }
    try {
      await api.post('/social/accounts/connect', connectDraft);
      toast.success('Account saved. Add your access token to enable live publishing.');
      setConnectOpen(false);
      setConnectDraft({ platform: 'tiktok', handle: '', access_token: '', account_id: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to connect account'); }
  };

  const disconnectAccount = async (id) => {
    if (!window.confirm('Disconnect this account?')) return;
    try {
      await api.delete(`/social/accounts/${id}`);
      toast.success('Disconnected');
      load();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="social-hub-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <h1 className="font-semibold text-slate-800" data-testid="social-hub-title">Social Hub</h1>
            <Badge variant="outline" className="text-xs">Beta</Badge>
          </div>
          <Button size="sm" variant="outline" onClick={() => setConnectOpen(true)} data-testid="connect-account-btn">
            <Plus className="w-4 h-4 mr-1" /> Connect account
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 md:p-6">
        {loading ? (
          <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>
        ) : (
          <>
            {summary && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <StatCard label="Drafts" value={summary.drafts} icon={<Send className="w-4 h-4" />} />
                <StatCard label="Scheduled" value={summary.scheduled} icon={<Clock className="w-4 h-4" />} />
                <StatCard label="Published" value={summary.published} icon={<CheckCircle2 className="w-4 h-4" />} />
                <StatCard label="Total posts" value={summary.total} icon={<Music className="w-4 h-4" />} />
              </div>
            )}

            {accounts.length === 0 && (
              <Card className="border-2 border-amber-200 bg-amber-50 mb-6">
                <CardContent className="p-4 flex items-start gap-3">
                  <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-amber-900">
                    <p className="font-semibold">No connected accounts yet.</p>
                    <p className="text-xs mt-1">
                      You can still draft and schedule posts. To actually publish, connect at least one platform account with an access token. See the setup guide at{' '}
                      <code className="bg-white px-1 rounded">/app/memory/SOCIAL_MEDIA_SETUP.md</code>.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            <Tabs value={tab} onValueChange={(v) => { setTab(v); if (v === 'templates' && templates.length === 0) loadTemplates(); }}>
              <TabsList>
                <TabsTrigger value="composer" data-testid="tab-composer">Composer</TabsTrigger>
                <TabsTrigger value="templates" data-testid="tab-templates">
                  <Sparkles className="w-3 h-3 mr-1" /> From Template
                </TabsTrigger>
                <TabsTrigger value="scheduled" data-testid="tab-scheduled">Scheduled &amp; Drafts</TabsTrigger>
                <TabsTrigger value="accounts" data-testid="tab-accounts">Connected accounts</TabsTrigger>
              </TabsList>

              <TabsContent value="composer">
                <Card className="mt-4">
                  <CardHeader>
                    <CardTitle className="text-lg">Compose a post</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label className="mb-2 block">Target platforms</Label>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(PLATFORM_META).map(([k, meta]) => {
                          const Icon = meta.icon;
                          const active = draft.platforms.includes(k);
                          return (
                            <button
                              key={k}
                              type="button"
                              onClick={() => togglePlatform(k)}
                              className={`px-3 py-2 rounded-full border-2 flex items-center gap-2 text-sm font-medium transition ${
                                active
                                  ? `${meta.color} border-transparent shadow-sm scale-105`
                                  : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                              }`}
                              data-testid={`platform-toggle-${k}`}
                            >
                              <Icon className="w-4 h-4" /> {meta.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="caption">Caption</Label>
                      <Textarea
                        id="caption"
                        value={draft.caption}
                        onChange={(e) => setDraft({ ...draft, caption: e.target.value })}
                        placeholder="Share what makes Zazu magic today…"
                        rows={5}
                        data-testid="composer-caption"
                      />
                      <p className="text-xs text-slate-400 mt-1">{draft.caption.length} chars</p>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="hashtags">Hashtags (space-separated, include #)</Label>
                        <Input
                          id="hashtags"
                          value={draft.hashtags}
                          onChange={(e) => setDraft({ ...draft, hashtags: e.target.value })}
                          placeholder="#EdTech #KidsLearning #HomeSchool"
                          data-testid="composer-hashtags"
                        />
                      </div>
                      <div>
                        <Label htmlFor="link_url">Link (Facebook &amp; Snapchat only)</Label>
                        <Input
                          id="link_url"
                          value={draft.link_url}
                          onChange={(e) => setDraft({ ...draft, link_url: e.target.value })}
                          placeholder="https://zazued.com/blog/…"
                          data-testid="composer-link"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="schedule_at">Schedule (leave blank to save as draft or publish now)</Label>
                      <Input
                        id="schedule_at"
                        type="datetime-local"
                        value={draft.schedule_at}
                        onChange={(e) => setDraft({ ...draft, schedule_at: e.target.value })}
                        data-testid="composer-schedule"
                      />
                    </div>

                    {/* Media attachments */}
                    <div>
                      <Label>Media (images or video)</Label>
                      <div className="flex items-center gap-2 flex-wrap mt-1">
                        {draft.media_urls.map((url) => (
                          <div key={url} className="relative border border-slate-300 rounded overflow-hidden bg-slate-100" data-testid={`media-chip-${url}`}>
                            <img src={url} alt="" className="w-20 h-20 object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                            <button
                              type="button"
                              onClick={() => removeMedia(url)}
                              className="absolute top-0 right-0 bg-black/70 text-white rounded-bl w-5 h-5 flex items-center justify-center"
                              data-testid="remove-media-btn"
                            >
                              <XIcon className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                        <label className="w-20 h-20 border-2 border-dashed border-slate-300 rounded flex flex-col items-center justify-center cursor-pointer hover:border-slate-400 hover:bg-slate-50 text-slate-500 text-xs">
                          {uploadingMedia ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mb-1" /><span>Add</span></>}
                          <input type="file" className="hidden" onChange={uploadMedia} accept="image/*,video/*" disabled={uploadingMedia} data-testid="composer-media-input" />
                        </label>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">Max 25 MB. Images (JPG/PNG/WebP) and videos (MP4/WebM).</p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" onClick={() => createPost(false)} disabled={saving} data-testid="save-draft-btn">
                        {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                        {draft.schedule_at ? 'Schedule' : 'Save draft'}
                      </Button>
                      <Button onClick={() => createPost(true)} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="publish-now-btn">
                        {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                        Publish now
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="templates">
                <Card className="mt-4">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2"><Sparkles className="w-4 h-4 text-purple-500" /> Post from a template</CardTitle>
                      <div className="text-xs text-slate-500">
                        Target platforms: {draft.platforms.length === 0
                          ? <span className="text-amber-600 font-semibold">Pick platforms in Composer first</span>
                          : draft.platforms.map((p) => PLATFORM_META[p]?.label || p).join(', ')}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-slate-600 mb-4">
                      Pick a Materials-Generator lesson you&apos;ve already made — Zazu writes a per-platform caption + hashtags + hook and drops it in the composer, respecting each platform&apos;s character limit.
                    </p>
                    {templatesLoading ? (
                      <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-purple-500" /></div>
                    ) : templates.length === 0 ? (
                      <div className="text-center py-10 border border-dashed border-slate-300 rounded">
                        <FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                        <p className="text-sm text-slate-500">No generated materials yet.</p>
                        <Button size="sm" variant="outline" className="mt-3" onClick={() => navigate('/materials')} data-testid="go-materials-btn">
                          Open Materials Generator <ExternalLink className="w-3 h-3 ml-1" />
                        </Button>
                      </div>
                    ) : (
                      <div className="grid md:grid-cols-2 gap-3">
                        {templates.map((t) => (
                          <div key={t.id} className={`border rounded-lg p-4 hover:border-purple-400 transition ${pickedTemplate === t.id ? 'border-purple-500 bg-purple-50' : 'border-slate-200'}`} data-testid={`template-card-${t.id}`}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                  <Badge variant="outline" className="text-xs">{t.kind}</Badge>
                                  <Badge className="bg-emerald-100 text-emerald-700 text-xs">{t.subject}</Badge>
                                  {t.grade_band && <Badge className="bg-blue-100 text-blue-700 text-xs">{t.grade_band}</Badge>}
                                </div>
                                <p className="font-semibold text-slate-800 truncate">{t.topic || 'Untitled'}</p>
                                <p className="text-xs text-slate-500 line-clamp-2 mt-1">{t.excerpt}</p>
                                <p className="text-xs text-slate-400 mt-1">{new Date(t.generated_at).toLocaleDateString()}</p>
                              </div>
                              <Button
                                size="sm"
                                onClick={() => generateFromTemplate(t.id)}
                                disabled={generatingCaption || draft.platforms.length === 0}
                                className="bg-purple-600 hover:bg-purple-700 text-white flex-shrink-0"
                                data-testid={`use-template-${t.id}`}
                              >
                                {generatingCaption ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                                <span className="ml-1">Use</span>
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="scheduled">
                <Card className="mt-4">
                  <CardHeader><CardTitle className="text-lg">Your posts</CardTitle></CardHeader>
                  <CardContent>
                    {posts.length === 0 ? (
                      <p className="text-sm text-slate-500 text-center py-8">No posts yet — write your first one in the Composer.</p>
                    ) : (
                      <div className="space-y-3">
                        {posts.map((p) => (
                          <div key={p.id} className="border border-slate-200 rounded-lg p-4" data-testid={`post-row-${p.id}`}>
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Badge className={STATUS_META[p.status]?.className || 'bg-slate-100'}>
                                    {STATUS_META[p.status]?.label || p.status}
                                  </Badge>
                                  {(p.platforms || []).map((pl) => {
                                    const Icon = PLATFORM_META[pl]?.icon || Music;
                                    return (
                                      <Badge key={pl} variant="outline" className="text-xs">
                                        <Icon className="w-3 h-3 mr-1" /> {PLATFORM_META[pl]?.label || pl}
                                      </Badge>
                                    );
                                  })}
                                  {p.schedule_at && <span className="text-xs text-slate-500">for {new Date(p.schedule_at).toLocaleString()}</span>}
                                </div>
                                <p className="mt-2 text-sm text-slate-800 line-clamp-3 whitespace-pre-wrap">{p.caption}</p>
                                {p.link_url && (
                                  <a href={p.link_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 underline mt-1 inline-flex items-center gap-1">
                                    <Link2 className="w-3 h-3" /> {p.link_url}
                                  </a>
                                )}
                                {p.publish_results && Object.keys(p.publish_results).length > 0 && (
                                  <div className="mt-2 space-y-1">
                                    {Object.entries(p.publish_results).map(([platform, res]) => (
                                      <div key={platform} className="text-xs flex items-center gap-2">
                                        {res.status === 'pending_credentials' && <AlertTriangle className="w-3 h-3 text-amber-500" />}
                                        {res.status === 'simulated' && <CheckCircle2 className="w-3 h-3 text-purple-500" />}
                                        {res.status === 'error' && <AlertTriangle className="w-3 h-3 text-red-500" />}
                                        <span className="font-medium">{PLATFORM_META[platform]?.label || platform}:</span>
                                        <span className="text-slate-600">{res.message}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div className="flex gap-1">
                                {['draft', 'scheduled', 'pending_credentials'].includes(p.status) && (
                                  <Button size="sm" variant="outline" onClick={() => publishPost(p.id)} data-testid={`publish-${p.id}`}>
                                    <Send className="w-3 h-3" />
                                  </Button>
                                )}
                                <Button size="sm" variant="ghost" onClick={() => deletePost(p.id)} data-testid={`delete-${p.id}`}>
                                  <Trash2 className="w-3 h-3 text-red-500" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="accounts">
                <Card className="mt-4">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">Connected accounts</CardTitle>
                      <a href="/SOCIAL_MEDIA_SETUP.md" target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" /> Setup guide
                      </a>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {accounts.length === 0 ? (
                      <p className="text-sm text-slate-500 text-center py-6">No connected accounts. Click <strong>Connect account</strong> above to add one.</p>
                    ) : (
                      <div className="space-y-2">
                        {accounts.map((acc) => {
                          const Icon = PLATFORM_META[acc.platform]?.icon || Music;
                          return (
                            <div key={acc.id} className="flex items-center justify-between border border-slate-200 rounded-lg p-3" data-testid={`account-row-${acc.id}`}>
                              <div className="flex items-center gap-3">
                                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${PLATFORM_META[acc.platform]?.color || 'bg-slate-100'}`}>
                                  <Icon className="w-4 h-4" />
                                </div>
                                <div>
                                  <p className="font-semibold text-sm">{acc.handle}</p>
                                  <p className="text-xs text-slate-500">{PLATFORM_META[acc.platform]?.label || acc.platform} · {acc.has_token ? 'API connected' : 'No token yet'}</p>
                                </div>
                              </div>
                              <Button size="sm" variant="ghost" onClick={() => disconnectAccount(acc.id)} data-testid={`disconnect-${acc.id}`}>
                                <Trash2 className="w-3 h-3 text-red-500" />
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </>
        )}
      </main>

      <Dialog open={connectOpen} onOpenChange={setConnectOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Connect a social account</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Platform</Label>
              <select
                className="w-full border border-slate-300 rounded-md px-3 py-2 mt-1"
                value={connectDraft.platform}
                onChange={(e) => setConnectDraft({ ...connectDraft, platform: e.target.value })}
                data-testid="connect-platform-select"
              >
                {Object.entries(PLATFORM_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <Label>Handle / username</Label>
              <Input value={connectDraft.handle} onChange={(e) => setConnectDraft({ ...connectDraft, handle: e.target.value })} placeholder="@zazueducation" data-testid="connect-handle" />
            </div>
            <div>
              <Label>Access token (optional — required to publish live)</Label>
              <Input value={connectDraft.access_token} onChange={(e) => setConnectDraft({ ...connectDraft, access_token: e.target.value })} placeholder="Paste from developer portal (see setup guide)" data-testid="connect-token" />
              <p className="text-xs text-slate-500 mt-1">Follow <code className="bg-slate-100 px-1 rounded">/app/memory/SOCIAL_MEDIA_SETUP.md</code> for step-by-step instructions.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConnectOpen(false)}>Cancel</Button>
            <Button onClick={connectAccount} data-testid="connect-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StatCard = ({ label, value, icon }) => (
  <Card className="border-slate-200"><CardContent className="p-3 flex items-center gap-3">
    <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600">{icon}</div>
    <div>
      <p className="text-2xl font-bold text-slate-900 leading-none">{value ?? 0}</p>
      <p className="text-xs text-slate-500 uppercase tracking-wide mt-1">{label}</p>
    </div>
  </CardContent></Card>
);

export default SocialHubPage;
